import React, {useContext} from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import {Paper, Table, TableHead, TableRow, TableCell, TableBody} from '@material-ui/core';
import {withStyles} from '@material-ui/core/styles';
import {isEmpty} from 'lodash/lang';
import {getColor} from 'utils/common';
import {I18nContext} from 'contexts/i18n';

const defaultHiddenFields = ['id', 'record'];

const buildTable = ({
  records,
  fieldsSettings,
  rowsSettings,
  extraHiddenFields = [],
  restProps,
  initialized,
  classes,
  i18nTranslate,
}) => {
  const headerRows = [];
  const bodyRows = [];
  const hiddenFields = new Set([...defaultHiddenFields, ...extraHiddenFields]);
  const fieldHeaderCellClassNames = {};
  const fieldBodyCellClassNames = {};

  const {onRowClick, sortingManager, onSortableFieldSet} = restProps;
  delete restProps.onRowClick;
  delete restProps.sortingManager;
  delete restProps.onSortableFieldSet;

  for (let fieldName of Object.keys(fieldsSettings)) {
    if (hiddenFields.has(fieldName)) {
      continue;
    }
    const field = fieldsSettings[fieldName];
    headerRows.push({
      name: fieldName,
      text: field.fieldText,
      extra: field.fieldExtra || '',
      sortable: field.sortable || false,
    });
    if (field.sortable && !sortingManager.fields[fieldName]) {
      sortingManager.register(fieldName);
    }
    fieldHeaderCellClassNames[fieldName] = field.headerCellClassName || {};
    fieldBodyCellClassNames[fieldName] = field.bodyCellClassName || {};
  }

  const tableHeader = (
    <TableHead className={classes.tableHeader}>
      <TableRow>
        {headerRows.map((field) => (
          <TableCell
            key={`header-cell-${field.text}`}
            className={clsx(classes.tableHeaderCell, classes.tableCell, fieldHeaderCellClassNames[field.name], {
              'header-cell-sortable': field.sortable,
            })}
            onClick={
              !field.sortable
                ? undefined
                : () =>
                    sortingManager
                      .clickCaret(field.name)
                      .then((sortingFieldStatus) => onSortableFieldSet(field.name, sortingFieldStatus))
            }
          >
            {field.text}
            {field.extra}
            {sortingManager && sortingManager.carets[field.name]}
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );

  if (isEmpty(records)) {
    return {
      tableHeader,
      tableBody: (
        <TableBody>
          <TableRow className={clsx(classes.tableBodyRow)}>
            <TableCell colSpan={headerRows.length} className={clsx(classes.tableCell)} style={{textAlign: 'center'}}>
              {!initialized ? i18nTranslate`Loading...` : i18nTranslate`No Data`}
            </TableCell>
          </TableRow>
        </TableBody>
      ),
    };
  }
  for (let record of records) {
    const row = [];
    for (let [fieldName, fieldValue] of Object.entries(record)) {
      if (hiddenFields.has(fieldName)) {
        continue;
      }
      if (fieldsSettings[fieldName].hasOwnProperty('formatData')) {
        fieldValue = fieldsSettings[fieldName].formatData(fieldValue, record, restProps);
      }
      row.push({
        name: fieldName,
        value: fieldValue,
      });
    }
    bodyRows.push([record.id, row]);
  }

  const tableBody = (
    <TableBody>
      {bodyRows.map(([id, row]) => {
        const {className: rowClassName} = rowsSettings[id] || {};
        return (
          <TableRow
            key={`body-${id}`}
            className={clsx(classes.tableBodyRow, rowClassName, {
              'cursor-pointer': !!onRowClick,
            })}
            onClick={onRowClick ? () => onRowClick(records.find((record) => record.id === id)) : undefined}
          >
            {row.map(({name, value}, idx) => (
              <TableCell key={`body-cell-${idx}`} className={clsx(classes.tableCell, fieldBodyCellClassNames[name])}>
                {value}
              </TableCell>
            ))}
          </TableRow>
        );
      })}
    </TableBody>
  );
  return {tableHeader, tableBody};
};

const withTableStyles = withStyles(({spacing, palette}) => ({
  paper: {
    width: '100%',
    overflowX: 'auto',
    marginBottom: 8,
  },
  tableActions: {
    flexBasis: 0,
    flexWrap: 'nowrap',
  },
  tableHeader: {
    backgroundColor: getColor({palette, lightModeColor: palette.common.white, darkModeColor: palette.primary.dark}),
  },
  tableBodyRow: {
    '&:hover': {
      backgroundColor: getColor({
        palette,
        lightModeColor: palette.highlight.main,
        darkModeColor: palette.highlight.light,
      }),
    },
  },
  tableHeaderCell: ({isDenseRow}) =>
    isDenseRow
      ? {
          lineHeight: 'unset',
          color: getColor({palette, lightModeColor: palette.common.black, darkModeColor: palette.common.white}),
        }
      : {},
  tableCell: ({isDenseRow}) => ({
    textAlign: 'center',
    ...(isDenseRow
      ? {
          fontSize: 18,
          fontWeight: 500,
          color: getColor({palette, lightModeColor: palette.common.black, darkModeColor: palette.common.white}),
          padding: '1px 3px',
        }
      : {
          fontSize: 14,
          padding: '15px 5px',
        }),
  }),
}));

const GreatTableCore = withTableStyles(
  ({records, fieldsSettings, rowsSettings, hiddenFields, initialized, classes, ...restProps}) => {
    const {i18nTranslate} = useContext(I18nContext);

    const {tableHeader, tableBody} = buildTable({
      records,
      fieldsSettings,
      rowsSettings,
      extraHiddenFields: hiddenFields,
      restProps,
      initialized,
      classes,
      i18nTranslate,
    });
    return (
      <div className={classes.paper}>
        <Table className={classes.table}>
          {tableHeader}
          {tableBody}
        </Table>
      </div>
    );
  },
);

GreatTableCore.propTypes = {
  initialized: PropTypes.bool.isRequired,
  records: PropTypes.array.isRequired,
  fieldsSettings: PropTypes.object.isRequired,
  rowsSettings: PropTypes.object.isRequired,
  hiddenFields: PropTypes.array,
};

GreatTableCore.defaultProps = {
  rowsSettings: {},
  hiddenFields: [],
};

export default GreatTableCore;
